export type BrowserContext = {
  platform?: string; // OS/platform hint
  osName?: string;
  device?: string;
  userAgent?: string;
  browserBrand?: string;
  osVersion?: string;
  scene?: string;
  assignmentId?: string;
  viewport?: string;
  deviceMemory?: number;
  hardwareConcurrency?: number;
  connection?: string;
  online?: boolean;
};

const memoizedStatic = (() => {
  let cached: Partial<BrowserContext> | null = null;
  return () => {
    if (cached) return cached;
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      cached = {};
      return cached;
    }
    const connection =
      (navigator as Navigator & { connection?: { effectiveType?: string } })
        ?.connection || undefined;
    const deviceMemory =
      typeof (navigator as Navigator & { deviceMemory?: number })
        .deviceMemory === 'number'
        ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory
        : undefined;
    const hardwareConcurrency =
      typeof navigator.hardwareConcurrency === 'number'
        ? navigator.hardwareConcurrency
        : undefined;

    const userAgentData = (
      navigator as Navigator & {
        userAgentData?: {
          platform?: string;
          platformVersion?: string;
          brands?: Array<{ brand?: string }>;
        };
      }
    ).userAgentData;

    const browserBrand = userAgentData?.brands?.[0]?.brand;
    const platform = userAgentData?.platform || navigator.platform || 'web';
    const osName = userAgentData?.platform || undefined;
    const userAgent = navigator.userAgent;

    cached = {
      platform,
      osName,
      device: platform,
      userAgent,
      browserBrand,
      osVersion: userAgentData?.platformVersion,
      deviceMemory,
      hardwareConcurrency,
      connection: connection?.effectiveType,
      online:
        typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined,
    };
    return cached;
  };
})();

export const getBrowserContext = (
  overrides?: Partial<BrowserContext>
): BrowserContext => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return overrides ?? {};
  }

  const staticContext = memoizedStatic();
  const parseAssignmentId = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const param =
        params.get('assignmentId') || params.get('assignment_id') || undefined;
      if (param) return param;
      const parts = window.location.pathname.split('/').filter(Boolean);
      const idx = parts.findIndex((p) => p === 'assignments');
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
      return undefined;
    } catch {
      return undefined;
    }
  };

  const assignmentId = parseAssignmentId();

  return {
    ...staticContext,
    scene: window.location.pathname,
    assignmentId,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ...overrides,
  };
};
